/*
 * @Author: youfanrong 737064439@qq.com
 * @Date: 2026-02-08 13:34:15
 * @LastEditors: youfanrong 737064439@qq.com
 * @LastEditTime: 2026-05-02 16:27:23
 * @FilePath: /bar-finance/src/permission.ts
 */
import router from './router'
import { useTitle } from '@/hooks/web/useTitle'
import { useNProgress } from '@/hooks/web/useNProgress'
import { useAppStoreWithOut } from '@/store/modules/app'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { usePageLoading } from '@/hooks/web/usePageLoading'
import { NO_REDIRECT_WHITE_LIST } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { getCurUserDetailApi } from '@/api/login'
import type { RouteRecordRaw } from 'vue-router'
import WsModule from '@/utils/wsModule'

const { start, done } = useNProgress()

const { loadStart, loadDone } = usePageLoading()

router.beforeEach(async (to, from, next) => {
  start()
  loadStart()
  const permissionStore = usePermissionStoreWithOut()
  const appStore = useAppStoreWithOut()
  const userStore = useUserStoreWithOut()
  const wsModule = WsModule

  // 独立企微入口携带的新 token 优先于财务中心可能残留的旧登录态。
  if (to.path === '/login' && to.query.token) {
    next()
    return
  }

  if (userStore.getToken) {
    // 已登录状态：访问 /login 时直接跳转到根路由
    if (to.path === '/login') {
      next({ path: '/' })
      return
    }

    // 已添加过动态路由，直接放行
    if (permissionStore.getIsAddRouters) {
      next()
      return
    }

    // 验证 token 有效性，获取最新用户信息和路由
    try {
      const res = await getCurUserDetailApi()
      if (res && res.code === 200) {
        wsModule.connectWebSocket()
        const roleRouters = res?.data?.perms?.filter((item) => item.platform === 3)
        userStore.setRoleRouters(roleRouters)
        userStore.setUserInfo(res?.data)

        if (appStore.getDynamicRouter) {
          appStore.serverDynamicRouter
            ? await permissionStore.generateRoutes(
                'server',
                roleRouters as AppCustomRouteRecordRaw[]
              )
            : await permissionStore.generateRoutes('frontEnd', roleRouters as string[])
        } else {
          await permissionStore.generateRoutes('static')
        }

        permissionStore.getAddRouters.forEach((route) => {
          router.addRoute(route as unknown as RouteRecordRaw)
        })

        permissionStore.setIsAddRouters(true)

        const redirectPath = from.query.redirect || to.query.redirect || to.path
        const redirect = decodeURIComponent(redirectPath as string)

        // 递归找到第一个可见、可导航的叶子路由，返回完整路径
        const findFirstPagePath = (routes: any[], parentPath = ''): string | null => {
          for (const route of routes) {
            if (route.meta?.hidden) continue
            const children = (route.children || []).filter((c: any) => !c.meta?.hidden)
            if (children.length > 0) {
              const childResult = findFirstPagePath(children, route.path || parentPath)
              if (childResult) return childResult
            }
            // 叶子路由：有效路径、非根、非通配符
            if (route.path && route.path !== '/' && !route.path.includes(':path')) {
              const base = route.path.startsWith('/')
                ? ''
                : (parentPath || '/').replace(/\/$/, '') + '/'
              return base + route.path
            }
          }
          return null
        }

        const firstPagePath = findFirstPagePath(permissionStore.addRouters)

        // 根路径重定向到第一个菜单页，否则走 redirect 或原路径
        if (to.path === '/') {
          next({ path: firstPagePath || '/', replace: true })
        } else if (to.query.redirect) {
          next({ path: redirect, replace: true })
        } else {
          next({ ...to, replace: true })
        }
      } else {
        // token 无效，清除登录状态，跳转登录页
        userStore.reset()
        next(`/login?redirect=${to.path}`)
      }
    } catch {
      // 请求异常（网络不通等），清除登录状态，跳转登录页
      userStore.reset()
      next(`/login?redirect=${to.path}`)
    }
  } else {
    // 未登录状态：白名单路径直接放行，否则跳转登录页
    if (NO_REDIRECT_WHITE_LIST.indexOf(to.path) !== -1) {
      next()
    } else {
      next(`/login?redirect=${to.path}`)
      wsModule.closeWebSocket()
    }
  }
})

router.afterEach((to) => {
  useTitle(to?.meta?.title as string)
  done()
  loadDone()
})
